import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type CourseMediaRow, type StudioCourse, type StudioLessonRow } from '../../lib/api.js'
import { Column, TopTitle } from '../../classroom/Shell.js'
import { ErrorLine, Skeleton, Stat, StatGrid, Status } from '../../classroom/ui.js'

/**
 * Kursy (Studio-Courses.png). Diana cannot edit lesson text here — the
 * Markdown files are the source of truth and the import keeps the database
 * in step with them — but she hangs on each lesson what the files cannot
 * carry: the recording, the pictures, the PDFs. Reading a course happens in
 * the lesson player, as a student does; her ADMIN role opens every course.
 */
/**
 * The natural voice that reads words and dialogues (apps/api/src/course/tts.ts):
 * how many of its recordings exist. The server makes them in the background.
 */
function VoiceStatus() {
  const { data } = useQuery({ queryKey: ['studio', 'tts'], queryFn: api.studio.ttsStatus, refetchInterval: 60_000 })
  if (!data) return null
  const n = (x: number) => x.toLocaleString('pl-PL')
  return (
    <div className="mt-3 border-t border-class-line px-3.5 pt-4 text-class-label text-class-ink-soft">
      <p className="m-0 font-semibold text-class-ink">Głos lektora</p>
      {!data.configured ? (
        <p className="m-0 mt-1">Czyta głos urządzenia ucznia. Naturalny głos ruszy po dodaniu klucza Google.</p>
      ) : (
        <>
          <p className="m-0 mt-1">
            {n(data.done)} z {n(data.total)} nagrań
            {data.generating ? ' · w toku' : data.done < data.total ? ' · reszta wkrótce' : ' · gotowe'}
          </p>
          <p className="m-0 mt-0.5 text-class-ink-faint">
            {n(data.monthChars)} z {n(data.cap)} znaków w tym miesiącu
          </p>
        </>
      )}
    </div>
  )
}

export function CoursesAdminPage() {
  const courses = useQuery({ queryKey: ['studio', 'courses'], queryFn: api.studio.courses })
  const [selected, setSelected] = useState<string | null>(null)
  const list = courses.data ?? []
  const course = list.find((c) => c.id === selected) ?? list.find((c) => c.published) ?? list[0]
  const firstLesson = useQuery({
    queryKey: ['studio', 'course', course?.id, 'lessons'],
    queryFn: () => api.studio.courseLessons(course!.id),
    enabled: Boolean(course),
  })

  return (
    <Column
      density="studio"
      top={
        <>
          <TopTitle studio>Kursy</TopTitle>
          {course && firstLesson.data?.[0] && (
            <Link to={`/panel/kurs/${course.slug}/${firstLesson.data[0].slug}`} className="ml-auto text-class-gloss font-semibold">
              Podgląd odtwarzacza lekcji ›
            </Link>
          )}
        </>
      }
    >
      {courses.isPending ? (
        <div className="p-10">
          <Skeleton lines={5} />
        </div>
      ) : list.length === 0 ? (
        <p className="m-0 p-10 text-class-ink-soft">Nie ma jeszcze żadnego kursu w bazie. Import uruchamia się przy starcie aplikacji.</p>
      ) : (
        <div className="flex min-h-full flex-col desk:flex-row">
          <nav aria-label="Kursy" className="flex shrink-0 flex-col gap-1 border-class-line p-3 desk:w-[258px] desk:border-r">
            {list.map((c) => {
              const on = c.id === course?.id
              return (
                <button
                  key={c.id}
                  type="button"
                  aria-current={on ? 'true' : undefined}
                  onClick={() => setSelected(c.id)}
                  className={[
                    'flex cursor-pointer flex-col items-start gap-0.5 rounded-class-btn border-0 px-3.5 py-3 text-left font-[inherit]',
                    on ? 'bg-class-white shadow-[inset_0_0_0_1px_var(--color-class-sky-200)]' : 'bg-transparent hover:bg-class-field-soft',
                  ].join(' ')}
                >
                  <span className="font-semibold text-class-ink">{c.title}</span>
                  {c.lessons === 0 ? (
                    <Status tone="neutral">W przygotowaniu</Status>
                  ) : c.published ? (
                    <Status tone="ok">Opublikowany</Status>
                  ) : (
                    <Status tone="wait">Szkic</Status>
                  )}
                </button>
              )
            })}
            <VoiceStatus />
          </nav>
          <div className="min-w-0 flex-1">{course && <CourseDetail key={course.id} course={course} />}</div>
        </div>
      )}
    </Column>
  )
}

function CourseDetail({ course }: { course: StudioCourse }) {
  const queryClient = useQueryClient()
  const lessons = useQuery({ queryKey: ['studio', 'course', course.id, 'lessons'], queryFn: () => api.studio.courseLessons(course.id) })
  const media = useQuery({ queryKey: ['studio', 'course', course.id, 'media'], queryFn: () => api.studio.courseMedia(course.id) })
  const students = useQuery({ queryKey: ['studio', 'course', course.id, 'students'], queryFn: () => api.studio.courseStudents(course.id) })
  const [error, setError] = useState('')
  const [report, setReport] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const uploadsRef = useRef<HTMLElement>(null)

  const refresh = () => {
    // Every course, and the list: a re-import can add a course or change any of them.
    queryClient.invalidateQueries({ queryKey: ['studio', 'course'] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'courses'] })
    queryClient.invalidateQueries({ queryKey: ['studio', 'dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['course'] })
  }
  const upload = useMutation({
    mutationFn: ({ file, lessonId, slot }: { file: File; lessonId: string | null; slot?: number }) =>
      api.studio.uploadCourseMedia(course.id, file, lessonId, slot),
    onSuccess: () => {
      setError('')
      refresh()
    },
    onError: () => setError('Nie udało się wgrać. Nagranie: MP3, M4A, OGG, WAV; ilustracja: PNG, JPG, WebP; albo PDF — do 60 MB.'),
  })
  const remove = useMutation({ mutationFn: api.studio.deleteCourseMedia, onSuccess: refresh })
  const reimport = useMutation({
    mutationFn: api.studio.importCourses,
    onSuccess: (r) => {
      setError('')
      setReport(r.summary)
      refresh()
    },
    onError: () => setError('Import się nie powiódł.'),
  })
  const busy = upload.isPending || remove.isPending || reimport.isPending

  // A row whose file is gone counts as not uploaded, so the lesson asks for it again.
  const all = (media.data ?? []).filter((m) => !m.missing)
  const audioById = new Map(all.filter((m) => m.kind === 'audio').map((m) => [m.id, m]))
  const hasAudio = (l: StudioLessonRow) => Boolean(l.audioMediaId && audioById.has(l.audioMediaId))
  const imageFor = new Map(all.filter((m) => m.kind === 'image').map((m) => [`${m.lessonId}:${m.slot}`, m]))
  const pdfs = all.filter((m) => m.kind === 'pdf')
  const rows = lessons.data ?? []
  const withAudio = rows.filter(hasAudio).length
  const needed = rows.reduce((n, l) => n + l.graphics.length, 0)
  const doneImages = rows.reduce((n, l) => n + l.graphics.filter((g) => imageFor.has(`${l.id}:${g}`)).length, 0)
  const activeStudents = (students.data ?? []).filter((s) => !s.revokedAt).length

  // The lesson to upload for: the one picked, else the first one still missing something.
  const missing = (l: StudioLessonRow) =>
    (course.hasListening && !hasAudio(l)) || l.graphics.some((g) => !imageFor.has(`${l.id}:${g}`))
  const current = rows.find((l) => l.id === open) ?? rows.find(missing) ?? rows[0]
  useEffect(() => {
    if (open) uploadsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [open])

  return (
    <main className="flex flex-col gap-12 px-5 pt-8 pb-14 desk:px-12 desk:pt-10">
      <section className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <h1 className="cl-title m-0">{course.title}</h1>
          <Link to={`/panel/kurs/${course.slug}`} className="ml-auto text-class-gloss font-semibold">
            Otwórz jako uczeń ›
          </Link>
        </div>
        <p className="m-0 flex flex-wrap gap-x-2 text-class-gloss text-class-ink-soft">
          <span>
            {course.level} · {course.lessons} lekcji · {activeStudents} {activeStudents === 1 ? 'uczeń' : 'uczniów'}
          </span>
          {course.importedAt && <span>· z plików {new Date(course.importedAt).toLocaleDateString('pl-PL')}</span>}
          <button type="button" className="cl-link-quiet text-class-gloss" disabled={busy} onClick={() => reimport.mutate()}>
            · {reimport.isPending ? 'Wczytuję…' : 'Wczytaj lekcje z plików'}
          </button>
        </p>
        {report && <pre className="m-0 mt-2 max-h-40 overflow-auto rounded-class-sm bg-class-field-soft p-3 text-class-label whitespace-pre-wrap">{report}</pre>}
        {error && <ErrorLine>{error}</ErrorLine>}
      </section>

      <StatGrid>
        {course.hasListening ? <Stat label="Nagrania" value={`${withAudio} / ${rows.length}`} /> : <Stat label="Nagrania" value="—" hint="kurs bez słuchania" />}
        <Stat label="Ilustracje" value={`${doneImages} / ${needed}`} />
        <Stat label="PDF" value={pdfs.length} hint={pdfs.length === 0 ? 'jeszcze żadnego' : undefined} />
      </StatGrid>

      <section className="flex flex-col">
        <h2 className="cl-subheading m-0 pb-3">Lekcje</h2>
        {lessons.isPending ? (
          <Skeleton />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-left text-class-gloss">
              <thead>
                <tr className="text-class-label font-semibold text-class-ink-faint">
                  <th className="pb-2.5 font-semibold">Lekcja</th>
                  <th className="w-28 pb-2.5 font-semibold">Nagranie</th>
                  <th className="w-24 pb-2.5 font-semibold">Ilustracje</th>
                  <th className="w-14 pb-2.5 font-semibold">PDF</th>
                  <th className="w-20" />
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const imgs = l.graphics.filter((g) => imageFor.has(`${l.id}:${g}`)).length
                  const hasPdf = pdfs.some((p) => p.lessonId === l.id)
                  return (
                    <tr key={l.id} className={['border-t border-class-line', current?.id === l.id ? 'bg-class-field-soft/60' : ''].join(' ')}>
                      <td className="py-3.5 pr-4">
                        <span className="text-class-ink-faint">{l.number} · </span>
                        <Link to={`/panel/kurs/${course.slug}/${l.slug}`} className="!text-class-ink hover:!text-class-sky-700" title="Otwórz lekcję tak, jak widzi ją uczeń">
                          {l.title}
                        </Link>
                      </td>
                      <td>{course.hasListening ? hasAudio(l) ? <Status tone="ok">Jest</Status> : <Status tone="bad">Brak</Status> : '—'}</td>
                      <td className={imgs < l.graphics.length ? 'text-class-ink' : 'text-class-ink-faint'}>{l.graphics.length ? `${imgs} / ${l.graphics.length}` : '—'}</td>
                      <td className="text-class-ink-faint">{hasPdf ? '✓' : ''}</td>
                      <td className="text-right">
                        <button type="button" className="cl-link text-class-gloss" onClick={() => setOpen(l.id)}>
                          Wgraj ›
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {current && (
        <section ref={uploadsRef} className="flex scroll-mt-6 flex-col">
          <h2 className="cl-subheading m-0 pb-3">
            Lekcja {current.number} · do wgrania
          </h2>
          <div className="flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
            {course.hasListening && (
              <UploadRow
                title="Nagranie · Słuchanie"
                meta="MP3, M4A, OGG albo WAV"
                accept="audio/mpeg,audio/mp4,audio/ogg,audio/wav"
                busy={busy}
                done={hasAudio(current)}
                onFile={(file) => upload.mutate({ file, lessonId: current.id })}
                preview={
                  current.audioMediaId && audioById.get(current.audioMediaId) ? (
                    <audio controls preload="none" src={`/api/course/media/${current.audioMediaId}`} className="h-8 max-w-[220px]" />
                  ) : null
                }
                onDelete={
                  current.audioMediaId
                    ? () => window.confirm(`Usunąć nagranie do lekcji ${current.number}?`) && remove.mutate(current.audioMediaId!)
                    : undefined
                }
              />
            )}
            {current.graphics.map((n) => {
              const img = imageFor.get(`${current.id}:${n}`)
              const note = current.graphicNotes?.[n]
              return (
                <UploadRow
                  key={n}
                  title={`Grafika ${n}${note?.title ? ` · ${note.title}` : ''}`}
                  meta={[note?.size, 'PNG, JPG albo WebP'].filter(Boolean).join(' · ')}
                  accept="image/png,image/jpeg,image/webp"
                  busy={busy}
                  done={Boolean(img)}
                  onFile={(file) => upload.mutate({ file, lessonId: current.id, slot: n })}
                  preview={
                    img ? (
                      <a href={`/api/course/media/${img.id}`} target="_blank" rel="noreferrer">
                        <img src={`/api/course/media/${img.id}`} alt={`Grafika ${n}`} className="h-10 w-16 rounded-md object-cover" />
                      </a>
                    ) : null
                  }
                  onDelete={img ? () => window.confirm(`Usunąć Grafikę ${n} z lekcji ${current.number}?`) && remove.mutate(img.id) : undefined}
                />
              )
            })}
            <UploadRow
              title="PDF do tej lekcji"
              meta={pdfs.filter((p) => p.lessonId === current.id).map((p) => p.filename).join(', ') || 'np. karta pracy'}
              accept="application/pdf"
              busy={busy}
              done={false}
              label="Dodaj PDF"
              onFile={(file) => upload.mutate({ file, lessonId: current.id })}
            />
          </div>
          {current.graphics.length === 0 && !course.hasListening && (
            <p className="m-0 mt-3 text-class-gloss text-class-ink-faint">Ta lekcja nie odsyła do żadnego obrazka.</p>
          )}
        </section>
      )}

      <section className="flex flex-col">
        <div className="flex items-baseline pb-3">
          <h2 className="cl-subheading m-0">Materiały PDF</h2>
          <FilePick accept="application/pdf" busy={busy} label="+ Dodaj PDF do kursu" onFile={(file) => upload.mutate({ file, lessonId: null })} className="ml-auto" />
        </div>
        {pdfs.length === 0 ? (
          <p className="m-0 border-t border-class-line pt-4 text-class-gloss text-class-ink-soft">Jeszcze żadnego. PDF trafia do ucznia na mapie kursu, do pobrania.</p>
        ) : (
          <div className="flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
            {pdfs.map((m) => (
              <PdfRow key={m.id} media={m} lessons={rows} busy={busy} onDelete={() => window.confirm('Usunąć ten PDF?') && remove.mutate(m.id)} />
            ))}
          </div>
        )}
      </section>
    </main>
  )
}

function UploadRow({
  title,
  meta,
  accept,
  busy,
  done,
  onFile,
  onDelete,
  preview,
  label,
}: {
  title: string
  meta: string
  accept: string
  busy: boolean
  done: boolean
  onFile: (file: File) => void
  onDelete?: () => void
  preview?: ReactNode
  label?: string
}) {
  return (
    <div className="flex min-h-[60px] flex-wrap items-center gap-x-4 gap-y-2 py-2">
      <span className="min-w-0 flex-1 leading-snug">
        <span className="block font-semibold">{title}</span>
        <span className="text-class-gloss text-class-ink-soft">{meta}</span>
      </span>
      {preview}
      {done && <Status tone="ok">jest</Status>}
      {onDelete && (
        <button type="button" className="cl-link-quiet text-class-label" disabled={busy} onClick={onDelete}>
          usuń
        </button>
      )}
      <FilePick accept={accept} busy={busy} label={label ?? (done ? 'Zamień' : 'Wybierz plik')} onFile={onFile} />
    </div>
  )
}

function FilePick({ accept, busy, label, onFile, className = '' }: { accept: string; busy: boolean; label: string; onFile: (file: File) => void; className?: string }) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onFile(file)
          e.target.value = ''
        }}
      />
      <button type="button" onClick={() => input.current?.click()} disabled={busy} className={`cl-link text-class-gloss ${className}`}>
        {label}
      </button>
    </>
  )
}

function PdfRow({ media, lessons, busy, onDelete }: { media: CourseMediaRow; lessons: { id: string; number: number }[]; busy: boolean; onDelete: () => void }) {
  const lesson = lessons.find((l) => l.id === media.lessonId)
  return (
    <div className="flex min-h-14 flex-wrap items-center gap-x-4 gap-y-1 py-2">
      <span className="min-w-0 flex-1 truncate font-semibold">{media.filename || 'PDF'}</span>
      <span className="text-class-gloss text-class-ink-faint">
        {lesson ? `lekcja ${lesson.number} · ` : 'cały kurs · '}
        {Math.round(media.sizeBytes / 1024)} KB
      </span>
      <a href={`/api/course/media/${media.id}`} className="text-class-gloss font-semibold">
        Pobierz
      </a>
      <button type="button" className="cl-link-quiet text-class-label" disabled={busy} onClick={onDelete}>
        usuń
      </button>
    </div>
  )
}
